/**
 * VCI-P1 Slice 1 — Internal Venue 360° Profile
 * Route: /venue-commerce/:vid
 *
 * Tabbed internal profile view for a single venue.
 * Internal UAT preview only. No public indexing.
 * Tabs: Overview | Spaces | Commercial | Media | Verification & Publication
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Hotel,
  MapPin,
  ArrowLeft,
  CheckCircle,
  Clock,
  Eye,
  EyeOff,
  Shield,
  Building2,
  Loader2,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  AlertCircle,
  Globe,
  Users,
  DollarSign,
  Tag,
  FileText,
  Image,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "overview",      label: "Overview",          icon: Building2 },
  { id: "spaces",        label: "Spaces",            icon: Hotel },
  { id: "commercial",    label: "Commercial",        icon: DollarSign },
  { id: "media",         label: "Media",             icon: Image },
  { id: "verification",  label: "Verification & Publication", icon: Shield },
];

const VENUE_TYPES = ["FARMHOUSE", "RESORT", "GARDEN", "BANQUET_HALL", "HOTEL", "HERITAGE", "OTHER"];
const SPACE_TYPES = ["LAWN", "BANQUET_HALL", "ROOFTOP", "POOL_SIDE", "CONFERENCE", "OTHER"];
const COMMISSION_TYPES = ["FIXED_PERCENT", "TIERED", "PER_EVENT"];

function VerificationBadge({ status }) {
  const cfg = {
    VERIFIED: { label: "Verified", icon: CheckCircle, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    VISITED:  { label: "Site Visited", icon: Eye, cls: "bg-blue-50 text-blue-700 border-blue-200" },
    PENDING:  { label: "Pending", icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  }[status] || { label: status || "Pending", icon: Clock, cls: "bg-gray-50 text-gray-500 border-gray-200" };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-medium ${cfg.cls}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
}

function SectionCard({ title, children, className = "" }) {
  return (
    <div className={`bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-4 ${className}`}>
      {title && <h3 className="text-sm font-semibold text-gray-700 border-b border-gray-50 pb-2">{title}</h3>}
      {children}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-start">
      <label className="text-xs font-medium text-gray-500 pt-2 col-span-1">{label}</label>
      <div className="col-span-2">{children}</div>
    </div>
  );
}

function toast(msg, type = "info") {
  // Minimal in-page toast replacement — relies on the platform toaster if loaded
  console[type === "error" ? "error" : "log"](`[Toast] ${msg}`);
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Venue360Profile() {
  const { vid } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/api/venue-commerce/venues/${vid}`, { withCredentials: true });
      setVenue(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load venue");
    } finally {
      setLoading(false);
    }
  }, [vid]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-gray-400 gap-2">
      <Loader2 size={20} className="animate-spin" />
      <span className="text-sm">Loading venue…</span>
    </div>
  );

  if (error) return (
    <div className="p-6">
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-red-700 flex items-center gap-3">
        <AlertCircle size={18} />
        <span className="text-sm">{error}</span>
      </div>
    </div>
  );

  if (!venue) return null;

  const verStatus = venue.verification?.status || "PENDING";
  const city = venue.geo_location?.city || venue.city || "—";

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/venue-commerce/catalogue")}
          className="gap-1 text-gray-500 hover:text-gray-700 -ml-1"
          data-testid="vci-back-to-catalogue"
        >
          <ArrowLeft size={14} /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center shadow-sm flex-shrink-0">
              <Hotel size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 truncate" data-testid="vci-venue-title">
                {venue.display_name || venue.company_name}
              </h1>
              <div className="flex items-center gap-2 mt-0.5">
                {venue.venue_slug && (
                  <span className="font-mono text-xs text-gray-400">{venue.venue_slug}</span>
                )}
                <VerificationBadge status={verStatus} />
                {venue.is_published ? (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border bg-green-50 text-green-700 border-green-200 font-medium">
                    <Eye size={10} /> Live (Internal)
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border bg-gray-50 text-gray-500 border-gray-200 font-medium">
                    <EyeOff size={10} /> Draft
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                  <Shield size={10} /> Internal Preview
                </span>
              </div>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          className="gap-1 text-gray-500"
          data-testid="vci-venue-refresh"
        >
          <RefreshCw size={14} />
        </Button>
        {venue.venue_slug && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/venue-preview/${venue.venue_slug}`)}
            className="gap-1 text-xs text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100"
            data-testid="vci-venue-preview-btn"
          >
            <Eye size={12} /> Preview Profile
          </Button>
        )}
      </div>

      {/* Quick facts */}
      <div className="grid grid-cols-4 gap-3">
        <QuickFact icon={MapPin} label="City" value={city} />
        <QuickFact icon={Hotel} label="Type" value={venue.venue_type?.replace(/_/g, " ") || "—"} />
        <QuickFact icon={Users} label="Spaces" value={(venue.venue_spaces || []).length} />
        <QuickFact
          icon={DollarSign}
          label="Indicative From"
          value={venue.pricing_indicative_from != null
            ? `${venue.pricing_currency || "INR"} ${Number(venue.pricing_indicative_from).toLocaleString("en-IN")}`
            : "—"}
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-100">
        <div className="flex gap-0">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                  activeTab === tab.id
                    ? "border-indigo-600 text-indigo-700"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200"
                }`}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`vci-tab-${tab.id}`}
              >
                <Icon size={14} /> {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Save message */}
      {saveMsg && (
        <div className={`text-sm px-4 py-2 rounded-lg ${saveMsg.startsWith("Error") ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
          {saveMsg}
        </div>
      )}

      {/* Tab panels */}
      {activeTab === "overview" && (
        <OverviewTab
          venue={venue}
          onSaved={load}
          saving={saving}
          setSaving={setSaving}
          setSaveMsg={setSaveMsg}
        />
      )}
      {activeTab === "spaces" && (
        <SpacesTab venue={venue} onSaved={load} setSaveMsg={setSaveMsg} />
      )}
      {activeTab === "commercial" && (
        <CommercialTab venue={venue} onSaved={load} setSaveMsg={setSaveMsg} />
      )}
      {activeTab === "media" && (
        <MediaTab venue={venue} onSaved={load} setSaveMsg={setSaveMsg} />
      )}
      {activeTab === "verification" && (
        <VerificationTab venue={venue} onSaved={load} setSaveMsg={setSaveMsg} />
      )}
    </div>
  );
}

function QuickFact({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={13} className="text-indigo-500" />
        <span className="text-xs text-gray-400 font-medium">{label}</span>
      </div>
      <p className="text-sm font-semibold text-gray-800 truncate">{value ?? "—"}</p>
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────

function OverviewTab({ venue, onSaved, saving, setSaving, setSaveMsg }) {
  const [form, setForm] = useState({
    venue_slug:              venue.venue_slug || "",
    venue_type:              venue.venue_type || "",
    managed_flag:            venue.managed_flag ?? false,
    pricing_indicative_from: venue.pricing_indicative_from ?? "",
    pricing_currency:        venue.pricing_currency || "INR",
    accommodation_rooms:     venue.accommodation_rooms ?? "",
    seo_title:               venue.seo_title || "",
    seo_description:         venue.seo_description || "",
    geo_location: {
      lat:        venue.geo_location?.lat ?? "",
      lng:        venue.geo_location?.lng ?? "",
      city:       venue.geo_location?.city || venue.city || "",
      state:      venue.geo_location?.state || "",
      locality:   venue.geo_location?.locality || "",
      airport_km: venue.geo_location?.airport_km ?? "",
      railway_km: venue.geo_location?.railway_km ?? "",
      metro_km:   venue.geo_location?.metro_km ?? "",
    },
    event_suitability: (venue.event_suitability || []).join(", "),
    style_tags:        (venue.style_tags || []).join(", "),
    facilities: {
      parking:             venue.facilities?.parking ?? false,
      ac_banquet:          venue.facilities?.ac_banquet ?? false,
      power_backup:        venue.facilities?.power_backup ?? false,
      catering_inhouse:    venue.facilities?.catering_inhouse ?? false,
      generator:           venue.facilities?.generator ?? false,
      valet:               venue.facilities?.valet ?? false,
      changing_rooms:      venue.facilities?.changing_rooms ?? false,
      guest_accommodation: venue.facilities?.guest_accommodation ?? false,
    },
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setGeo = (k, v) => setForm(f => ({ ...f, geo_location: { ...f.geo_location, [k]: v } }));
  const setFacility = (k, v) => setForm(f => ({ ...f, facilities: { ...f.facilities, [k]: v } }));

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      const payload = {};
      if (form.venue_slug) payload.venue_slug = form.venue_slug;
      if (form.venue_type) payload.venue_type = form.venue_type;
      if (form.managed_flag !== venue.managed_flag) payload.managed_flag = form.managed_flag;
      if (form.pricing_indicative_from !== "") payload.pricing_indicative_from = parseFloat(form.pricing_indicative_from);
      if (form.pricing_currency) payload.pricing_currency = form.pricing_currency;
      if (form.accommodation_rooms !== "") payload.accommodation_rooms = parseInt(form.accommodation_rooms);
      if (form.seo_title) payload.seo_title = form.seo_title;
      if (form.seo_description) payload.seo_description = form.seo_description;
      if (Object.values(form.geo_location).some(v => v !== "" && v !== null)) {
        const geo = {};
        Object.entries(form.geo_location).forEach(([k, v]) => {
          if (v !== "") geo[k] = ["lat", "lng", "airport_km", "railway_km", "metro_km"].includes(k)
            ? parseFloat(v) : v;
        });
        payload.geo_location = geo;
      }
      if (form.event_suitability.trim()) {
        payload.event_suitability = form.event_suitability.split(",").map(s => s.trim().toUpperCase()).filter(Boolean);
      }
      if (form.style_tags.trim()) {
        payload.style_tags = form.style_tags.split(",").map(s => s.trim().toUpperCase()).filter(Boolean);
      }
      payload.facilities = form.facilities;

      await axios.patch(`${API}/api/venue-commerce/venues/${venue.id}/profile`, payload, { withCredentials: true });
      setSaveMsg("Venue profile saved successfully.");
      onSaved();
    } catch (err) {
      setSaveMsg(`Error: ${err.response?.data?.detail || "Save failed"}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Identity & Type */}
      <SectionCard title="Identity & Classification">
        <FieldRow label="Venue Slug">
          <Input
            value={form.venue_slug}
            onChange={e => set("venue_slug", e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""))}
            placeholder="e.g. heritage-garden-hyderabad"
            className="h-8 text-sm font-mono"
            data-testid="vci-field-venue-slug"
          />
          <p className="text-xs text-gray-400 mt-1">URL-safe, unique within tenant. Lowercase, hyphens only.</p>
        </FieldRow>
        <FieldRow label="Venue Type">
          <select
            value={form.venue_type}
            onChange={e => set("venue_type", e.target.value)}
            className="w-full h-8 border border-gray-200 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            data-testid="vci-field-venue-type"
          >
            <option value="">— Select —</option>
            {VENUE_TYPES.map(t => (
              <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
            ))}
          </select>
        </FieldRow>
        <FieldRow label="Managed">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.managed_flag}
              onChange={e => set("managed_flag", e.target.checked)}
              className="w-4 h-4 rounded accent-indigo-600"
              data-testid="vci-field-managed-flag"
            />
            <span className="text-sm text-gray-700">Full-service managed venue</span>
          </label>
        </FieldRow>
      </SectionCard>

      {/* Pricing */}
      <SectionCard title="Pricing">
        <div className="grid grid-cols-2 gap-4">
          <FieldRow label="Indicative From">
            <Input
              type="number"
              min={0}
              value={form.pricing_indicative_from}
              onChange={e => set("pricing_indicative_from", e.target.value)}
              placeholder="e.g. 500000"
              className="h-8 text-sm"
              data-testid="vci-field-pricing-from"
            />
          </FieldRow>
          <FieldRow label="Currency">
            <Input
              value={form.pricing_currency}
              onChange={e => set("pricing_currency", e.target.value.toUpperCase())}
              maxLength={3}
              className="h-8 text-sm"
              data-testid="vci-field-pricing-currency"
            />
          </FieldRow>
        </div>
        <FieldRow label="Guest Accommodation">
          <Input
            type="number"
            min={0}
            value={form.accommodation_rooms}
            onChange={e => set("accommodation_rooms", e.target.value)}
            placeholder="Number of rooms"
            className="h-8 text-sm"
            data-testid="vci-field-accommodation-rooms"
          />
        </FieldRow>
      </SectionCard>

      {/* Geo */}
      <SectionCard title="Location">
        <div className="grid grid-cols-2 gap-3">
          {["city", "state", "locality"].map(k => (
            <FieldRow key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
              <Input
                value={form.geo_location[k]}
                onChange={e => setGeo(k, e.target.value)}
                className="h-8 text-sm"
                data-testid={`vci-geo-${k}`}
              />
            </FieldRow>
          ))}
          <FieldRow label="Lat">
            <Input type="number" step="any" value={form.geo_location.lat} onChange={e => setGeo("lat", e.target.value)} className="h-8 text-sm" data-testid="vci-geo-lat" />
          </FieldRow>
          <FieldRow label="Lng">
            <Input type="number" step="any" value={form.geo_location.lng} onChange={e => setGeo("lng", e.target.value)} className="h-8 text-sm" data-testid="vci-geo-lng" />
          </FieldRow>
          {["airport_km", "railway_km", "metro_km"].map(k => (
            <FieldRow key={k} label={k.replace(/_km/, "").replace(/_/, " ").replace(/\b\w/g, c => c.toUpperCase()) + " (km)"}>
              <Input type="number" min={0} step="any" value={form.geo_location[k]} onChange={e => setGeo(k, e.target.value)} className="h-8 text-sm" data-testid={`vci-geo-${k}`} />
            </FieldRow>
          ))}
        </div>
      </SectionCard>

      {/* Facilities */}
      <SectionCard title="Facilities">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(form.facilities).map(([key, val]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer py-1">
              <input
                type="checkbox"
                checked={val}
                onChange={e => setFacility(key, e.target.checked)}
                className="w-4 h-4 rounded accent-indigo-600"
                data-testid={`vci-facility-${key}`}
              />
              <span className="text-sm text-gray-700">{key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
            </label>
          ))}
        </div>
      </SectionCard>

      {/* Discovery */}
      <SectionCard title="Discovery & SEO (Internal Prep)">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 flex items-start gap-2 mb-3">
          <Shield size={13} className="flex-shrink-0 mt-0.5" />
          SEO fields are for internal content preparation only. Public indexing is not enabled in Phase 1.
        </div>
        <FieldRow label="Event Suitability">
          <Input
            value={form.event_suitability}
            onChange={e => set("event_suitability", e.target.value)}
            placeholder="WEDDING_CEREMONY, SANGEET, RECEPTION"
            className="h-8 text-sm"
            data-testid="vci-field-event-suitability"
          />
          <p className="text-xs text-gray-400 mt-1">Comma-separated values</p>
        </FieldRow>
        <FieldRow label="Style Tags">
          <Input
            value={form.style_tags}
            onChange={e => set("style_tags", e.target.value)}
            placeholder="HERITAGE, GARDEN, LUXURY"
            className="h-8 text-sm"
            data-testid="vci-field-style-tags"
          />
        </FieldRow>
        <FieldRow label="SEO Title">
          <Input value={form.seo_title} onChange={e => set("seo_title", e.target.value)} placeholder="Internal SEO prep title" className="h-8 text-sm" data-testid="vci-field-seo-title" />
        </FieldRow>
        <FieldRow label="SEO Description">
          <Textarea value={form.seo_description} onChange={e => set("seo_description", e.target.value)} rows={2} className="text-sm resize-none" data-testid="vci-field-seo-description" />
        </FieldRow>
      </SectionCard>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="gap-2"
          data-testid="vci-overview-save-btn"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Venue Profile
        </Button>
      </div>
    </div>
  );
}

// ── Spaces Tab ────────────────────────────────────────────────────────────────

function SpacesTab({ venue, onSaved, setSaveMsg }) {
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({
    name: "", space_type: "", capacity_seated: "", capacity_floating: "",
    dimensions_sqft: "", is_independent_booking: false, media_url: "",
  });

  const handleAdd = async () => {
    if (!form.name.trim()) { setSaveMsg("Error: Space name is required"); return; }
    setAdding(true);
    setSaveMsg("");
    try {
      const payload = {
        name: form.name,
        space_type: form.space_type || null,
        capacity_seated: form.capacity_seated !== "" ? parseInt(form.capacity_seated) : null,
        capacity_floating: form.capacity_floating !== "" ? parseInt(form.capacity_floating) : null,
        dimensions_sqft: form.dimensions_sqft !== "" ? parseFloat(form.dimensions_sqft) : null,
        is_independent_booking: form.is_independent_booking,
        media_url: form.media_url || null,
      };
      await axios.post(`${API}/api/venue-commerce/venues/${venue.id}/spaces`, payload, { withCredentials: true });
      setSaveMsg("Space added successfully.");
      setForm({ name: "", space_type: "", capacity_seated: "", capacity_floating: "", dimensions_sqft: "", is_independent_booking: false, media_url: "" });
      onSaved();
    } catch (err) {
      setSaveMsg(`Error: ${err.response?.data?.detail || "Failed to add space"}`);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (space_id) => {
    setDeleting(space_id);
    setSaveMsg("");
    try {
      await axios.delete(`${API}/api/venue-commerce/venues/${venue.id}/spaces/${space_id}`, { withCredentials: true });
      setSaveMsg("Space removed.");
      onSaved();
    } catch (err) {
      setSaveMsg(`Error: ${err.response?.data?.detail || "Failed to remove space"}`);
    } finally {
      setDeleting(null);
    }
  };

  const spaces = venue.venue_spaces || [];

  return (
    <div className="space-y-5">
      <SectionCard title={`Venue Spaces (${spaces.length})`}>
        {spaces.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-2">No spaces defined yet.</p>
        ) : (
          <div className="space-y-2">
            {spaces.map(sp => (
              <div key={sp.space_id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3" data-testid={`vci-space-${sp.space_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate">{sp.name}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                    {sp.space_type && <span>{sp.space_type.replace(/_/g, " ")}</span>}
                    {sp.capacity_seated != null && <span>{sp.capacity_seated} seated</span>}
                    {sp.capacity_floating != null && <span>{sp.capacity_floating} floating</span>}
                    {sp.dimensions_sqft != null && <span>{sp.dimensions_sqft} sqft</span>}
                    {sp.is_independent_booking && (
                      <Badge variant="outline" className="text-xs px-1.5 py-0 border-indigo-200 text-indigo-600">Independent</Badge>
                    )}
                  </div>
                </div>
                <button
                  className="text-gray-400 hover:text-red-500 ml-3 p-1 rounded transition-colors"
                  onClick={() => handleRemove(sp.space_id)}
                  disabled={deleting === sp.space_id}
                  data-testid={`vci-remove-space-${sp.space_id}`}
                >
                  {deleting === sp.space_id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Add New Space">
        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Name *">
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Main Lawn" className="h-8 text-sm" data-testid="vci-new-space-name" />
          </FieldRow>
          <FieldRow label="Type">
            <select
              value={form.space_type}
              onChange={e => setForm(f => ({ ...f, space_type: e.target.value }))}
              className="w-full h-8 border border-gray-200 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              data-testid="vci-new-space-type"
            >
              <option value="">— Select —</option>
              {SPACE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Capacity (Seated)">
            <Input type="number" min={0} value={form.capacity_seated} onChange={e => setForm(f => ({ ...f, capacity_seated: e.target.value }))} className="h-8 text-sm" data-testid="vci-new-space-seated" />
          </FieldRow>
          <FieldRow label="Capacity (Floating)">
            <Input type="number" min={0} value={form.capacity_floating} onChange={e => setForm(f => ({ ...f, capacity_floating: e.target.value }))} className="h-8 text-sm" data-testid="vci-new-space-floating" />
          </FieldRow>
          <FieldRow label="Dimensions (sqft)">
            <Input type="number" min={0} step="any" value={form.dimensions_sqft} onChange={e => setForm(f => ({ ...f, dimensions_sqft: e.target.value }))} className="h-8 text-sm" data-testid="vci-new-space-sqft" />
          </FieldRow>
          <FieldRow label="Independent Booking">
            <label className="flex items-center gap-2 cursor-pointer pt-1">
              <input type="checkbox" checked={form.is_independent_booking} onChange={e => setForm(f => ({ ...f, is_independent_booking: e.target.checked }))} className="w-4 h-4 rounded accent-indigo-600" data-testid="vci-new-space-independent" />
              <span className="text-sm text-gray-700">Can be booked independently</span>
            </label>
          </FieldRow>
        </div>
        <div className="flex justify-end mt-2">
          <Button onClick={handleAdd} disabled={adding} size="sm" className="gap-2" data-testid="vci-add-space-btn">
            {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Add Space
          </Button>
        </div>
      </SectionCard>
    </div>
  );
}

// ── Commercial Tab ────────────────────────────────────────────────────────────

function CommercialTab({ venue, onSaved, setSaveMsg }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    commission_type: "FIXED_PERCENT",
    commission_rate: "",
    effective_from: "",
    effective_until: "",
    notes: "",
  });

  const agreements = venue.commercial_agreements || [];
  const active = agreements.filter(a => a.is_active);
  const inactive = agreements.filter(a => !a.is_active);

  const handleAdd = async () => {
    if (!form.effective_from) { setSaveMsg("Error: Effective from date is required"); return; }
    setSaving(true);
    setSaveMsg("");
    try {
      const payload = {
        commission_type: form.commission_type,
        commission_rate: form.commission_rate !== "" ? parseFloat(form.commission_rate) : null,
        effective_from: form.effective_from,
        effective_until: form.effective_until || null,
        notes: form.notes || null,
      };
      await axios.post(`${API}/api/venue-commerce/venues/${venue.id}/commercial-agreements`, payload, { withCredentials: true });
      setSaveMsg("Commercial agreement added.");
      setForm({ commission_type: "FIXED_PERCENT", commission_rate: "", effective_from: "", effective_until: "", notes: "" });
      onSaved();
    } catch (err) {
      setSaveMsg(`Error: ${err.response?.data?.detail || "Failed to add agreement"}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (agr_id) => {
    setSaveMsg("");
    try {
      await axios.patch(`${API}/api/venue-commerce/venues/${venue.id}/commercial-agreements/${agr_id}/deactivate`, {}, { withCredentials: true });
      setSaveMsg("Agreement deactivated.");
      onSaved();
    } catch (err) {
      setSaveMsg(`Error: ${err.response?.data?.detail || "Failed to deactivate"}`);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 flex items-start gap-2">
        <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
        <span>Commercial agreements are recorded for reference only. <strong>No commission calculations or GL postings</strong> are performed in Phase 1.</span>
      </div>

      {/* Active agreements */}
      <SectionCard title={`Active Agreements (${active.length})`}>
        {active.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No active agreements.</p>
        ) : (
          <div className="space-y-2">
            {active.map(agr => (
              <AgreementRow key={agr.agreement_id} agr={agr} onDeactivate={handleDeactivate} />
            ))}
          </div>
        )}
      </SectionCard>

      {/* Add new */}
      <SectionCard title="Add Commercial Agreement">
        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Commission Type">
            <select
              value={form.commission_type}
              onChange={e => setForm(f => ({ ...f, commission_type: e.target.value }))}
              className="w-full h-8 border border-gray-200 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              data-testid="vci-agr-commission-type"
            >
              {COMMISSION_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Rate (%)">
            <Input
              type="number" min={0} max={100} step="0.01"
              value={form.commission_rate}
              onChange={e => setForm(f => ({ ...f, commission_rate: e.target.value }))}
              placeholder="e.g. 8.5"
              className="h-8 text-sm"
              data-testid="vci-agr-commission-rate"
            />
          </FieldRow>
          <FieldRow label="Effective From *">
            <Input
              type="date"
              value={form.effective_from}
              onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))}
              className="h-8 text-sm"
              data-testid="vci-agr-effective-from"
            />
          </FieldRow>
          <FieldRow label="Effective Until">
            <Input
              type="date"
              value={form.effective_until}
              onChange={e => setForm(f => ({ ...f, effective_until: e.target.value }))}
              className="h-8 text-sm"
              data-testid="vci-agr-effective-until"
            />
            <p className="text-xs text-gray-400 mt-1">Leave blank for open-ended</p>
          </FieldRow>
          <FieldRow label="Notes">
            <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-8 text-sm" data-testid="vci-agr-notes" />
          </FieldRow>
        </div>
        <div className="flex justify-end mt-2">
          <Button onClick={handleAdd} disabled={saving} size="sm" className="gap-2" data-testid="vci-add-agreement-btn">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Add Agreement
          </Button>
        </div>
      </SectionCard>

      {/* History */}
      {inactive.length > 0 && (
        <SectionCard title={`Agreement History (${inactive.length} inactive)`}>
          <div className="space-y-2 opacity-60">
            {inactive.map(agr => <AgreementRow key={agr.agreement_id} agr={agr} readOnly />)}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

function AgreementRow({ agr, onDeactivate, readOnly }) {
  return (
    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3" data-testid={`vci-agreement-${agr.agreement_id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs px-1.5 py-0 border-indigo-200 text-indigo-700">
            v{agr.version}
          </Badge>
          <span className="text-sm font-medium text-gray-800">{agr.commission_type.replace(/_/g, " ")}</span>
          {agr.commission_rate != null && (
            <span className="text-sm text-gray-600">{agr.commission_rate}%</span>
          )}
          {!agr.is_active && (
            <Badge variant="outline" className="text-xs px-1.5 py-0 border-gray-200 text-gray-500">Inactive</Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          {agr.effective_from} → {agr.effective_until || "open-ended"}
        </p>
        {agr.notes && <p className="text-xs text-gray-400 italic mt-0.5">{agr.notes}</p>}
      </div>
      {!readOnly && agr.is_active && (
        <button
          className="text-gray-400 hover:text-red-500 ml-3 p-1 rounded transition-colors text-xs font-medium border border-gray-200 hover:border-red-200 px-2"
          onClick={() => onDeactivate(agr.agreement_id)}
          data-testid={`vci-deactivate-agreement-${agr.agreement_id}`}
        >
          Deactivate
        </button>
      )}
    </div>
  );
}

// ── Media Tab ─────────────────────────────────────────────────────────────────

function MediaTab({ venue, onSaved, setSaveMsg }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ url: "", media_type: "IMAGE", label: "", is_cover: false });
  const gallery = venue.media_gallery || [];

  const handleSave = async () => {
    if (!form.url.trim()) { setSaveMsg("Error: Media URL is required"); return; }
    setSaving(true);
    setSaveMsg("");
    try {
      const currentGallery = gallery.map(m => ({
        url: m.url, media_type: m.media_type, label: m.label, is_cover: m.is_cover,
      }));
      const newItem = { url: form.url, media_type: form.media_type, label: form.label || null, is_cover: form.is_cover };
      await axios.patch(
        `${API}/api/venue-commerce/venues/${venue.id}/profile`,
        { media_gallery: [...currentGallery, newItem] },
        { withCredentials: true },
      );
      setSaveMsg("Media item added.");
      setForm({ url: "", media_type: "IMAGE", label: "", is_cover: false });
      onSaved();
    } catch (err) {
      setSaveMsg(`Error: ${err.response?.data?.detail || "Failed to save media"}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <SectionCard title={`Media Gallery (${gallery.length})`}>
        {gallery.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No media items yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {gallery.map((m, i) => (
              <div key={i} className="relative bg-gray-100 rounded-lg aspect-video flex flex-col items-center justify-center overflow-hidden" data-testid={`vci-media-${i}`}>
                {m.media_type === "IMAGE" ? (
                  <img src={m.url} alt={m.label || "Venue media"} className="w-full h-full object-cover" onError={e => { e.target.style.display="none"; }} />
                ) : (
                  <div className="text-gray-400 text-xs text-center p-2">
                    <Globe size={20} className="mx-auto mb-1" />
                    Video
                  </div>
                )}
                {m.is_cover && (
                  <span className="absolute top-1 right-1 text-xs bg-indigo-600 text-white px-1.5 py-0.5 rounded font-medium">Cover</span>
                )}
                {m.label && (
                  <p className="absolute bottom-0 left-0 right-0 text-xs text-white bg-black/50 px-2 py-1 truncate">{m.label}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Add Media Item">
        <FieldRow label="URL *">
          <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://..." className="h-8 text-sm" data-testid="vci-media-url" />
        </FieldRow>
        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Type">
            <select value={form.media_type} onChange={e => setForm(f => ({ ...f, media_type: e.target.value }))} className="w-full h-8 border border-gray-200 rounded-lg px-2 text-sm focus:outline-none" data-testid="vci-media-type">
              <option value="IMAGE">Image</option>
              <option value="VIDEO">Video</option>
            </select>
          </FieldRow>
          <FieldRow label="Label">
            <Input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Aerial view" className="h-8 text-sm" data-testid="vci-media-label" />
          </FieldRow>
        </div>
        <FieldRow label="Cover Photo">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_cover} onChange={e => setForm(f => ({ ...f, is_cover: e.target.checked }))} className="w-4 h-4 rounded accent-indigo-600" data-testid="vci-media-is-cover" />
            <span className="text-sm text-gray-700">Mark as cover image</span>
          </label>
        </FieldRow>
        <div className="flex justify-end mt-2">
          <Button onClick={handleSave} disabled={saving} size="sm" className="gap-2" data-testid="vci-add-media-btn">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Add Media
          </Button>
        </div>
      </SectionCard>
    </div>
  );
}

// ── Verification & Publication Tab ────────────────────────────────────────────

function VerificationTab({ venue, onSaved, setSaveMsg }) {
  const [savingVerify, setSavingVerify] = useState(false);
  const [savingPublish, setSavingPublish] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState(venue.verification?.status || "PENDING");
  const [verifyNotes, setVerifyNotes] = useState("");

  const verification = venue.verification || {};
  const verStatus = verification.status || "PENDING";

  const handleVerify = async () => {
    setSavingVerify(true);
    setSaveMsg("");
    try {
      await axios.patch(
        `${API}/api/venue-commerce/venues/${venue.id}/verify`,
        { status: verifyStatus, notes: verifyNotes || null },
        { withCredentials: true },
      );
      setSaveMsg(`Verification status set to ${verifyStatus}.`);
      onSaved();
    } catch (err) {
      setSaveMsg(`Error: ${err.response?.data?.detail || "Failed to update verification"}`);
    } finally {
      setSavingVerify(false);
    }
  };

  const handlePublish = async (isPublished) => {
    setSavingPublish(true);
    setSaveMsg("");
    try {
      await axios.patch(
        `${API}/api/venue-commerce/venues/${venue.id}/publish`,
        { is_published: isPublished },
        { withCredentials: true },
      );
      setSaveMsg(isPublished ? "Venue published (internal preview)." : "Venue unpublished.");
      onSaved();
    } catch (err) {
      setSaveMsg(`Error: ${err.response?.data?.detail || "Failed to update publication"}`);
    } finally {
      setSavingPublish(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Verification workflow notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <p className="font-semibold mb-1">Verification is separate from Publication</p>
        <p className="text-xs text-blue-700">A venue must reach <strong>VERIFIED</strong> status before it can be published. Setting VERIFIED does not automatically publish.</p>
      </div>

      {/* Current verification status */}
      <SectionCard title="Verification Status">
        <div className="flex items-center gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Current status</p>
            <VerificationBadge status={verStatus} />
          </div>
          {verification.verified_by && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Verified by</p>
              <p className="text-sm text-gray-700">{verification.verified_by}</p>
            </div>
          )}
          {verification.verified_at && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Verified at</p>
              <p className="text-sm text-gray-700">{new Date(verification.verified_at).toLocaleDateString()}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Update Status">
            <select
              value={verifyStatus}
              onChange={e => setVerifyStatus(e.target.value)}
              className="w-full h-8 border border-gray-200 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              data-testid="vci-verify-status-select"
            >
              <option value="PENDING">Pending</option>
              <option value="VISITED">Site Visited</option>
              <option value="VERIFIED">Verified</option>
            </select>
          </FieldRow>
          <FieldRow label="Notes">
            <Input
              value={verifyNotes}
              onChange={e => setVerifyNotes(e.target.value)}
              placeholder="Optional notes…"
              className="h-8 text-sm"
              data-testid="vci-verify-notes"
            />
          </FieldRow>
        </div>
        <div className="flex justify-end mt-3">
          <Button
            onClick={handleVerify}
            disabled={savingVerify}
            size="sm"
            className="gap-2"
            data-testid="vci-save-verification-btn"
          >
            {savingVerify ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
            Save Verification
          </Button>
        </div>
      </SectionCard>

      {/* Publication */}
      <SectionCard title="Publication (Internal Preview)">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 flex items-start gap-2 mb-3">
          <Shield size={13} className="flex-shrink-0 mt-0.5" />
          All VCI Phase 1 venues are internal preview only. Public indexing is not enabled.
          The X-Robots-Tag header is set to noindex on all venue responses.
        </div>

        <div className="flex items-center gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Publication Status</p>
            {venue.is_published ? (
              <span className="inline-flex items-center gap-1 text-sm px-3 py-1 rounded border bg-green-50 text-green-700 border-green-200 font-medium">
                <Eye size={13} /> Live (Internal)
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-sm px-3 py-1 rounded border bg-gray-50 text-gray-500 border-gray-200 font-medium">
                <EyeOff size={13} /> Draft
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          {!venue.is_published ? (
            <Button
              onClick={() => handlePublish(true)}
              disabled={savingPublish || verStatus !== "VERIFIED"}
              size="sm"
              className="gap-2"
              data-testid="vci-publish-btn"
            >
              {savingPublish ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
              Publish (Internal)
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => handlePublish(false)}
              disabled={savingPublish}
              size="sm"
              className="gap-2"
              data-testid="vci-unpublish-btn"
            >
              {savingPublish ? <Loader2 size={13} className="animate-spin" /> : <EyeOff size={13} />}
              Unpublish
            </Button>
          )}
        </div>

        {verStatus !== "VERIFIED" && !venue.is_published && (
          <p className="text-xs text-amber-700 mt-2 flex items-center gap-1">
            <AlertCircle size={11} />
            Venue must be VERIFIED before it can be published.
          </p>
        )}
      </SectionCard>
    </div>
  );
}
